import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Calendar, Clock, Users, Loader2, ArrowRight, Copy, Check, AlertTriangle, CheckCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import api from '../lib/api';
import { formatCurrency, formatDate } from '../lib/utils';

const statusColors = {
  pending_approval: 'bg-amber-100 text-amber-800',
  approved: 'bg-blue-100 text-blue-800',
  deposit_paid: 'bg-emerald-100 text-emerald-800',
  fully_paid: 'bg-green-100 text-green-800',
  expired: 'bg-gray-100 text-gray-600',
  rejected: 'bg-red-100 text-red-700',
  cancelled: 'bg-red-50 text-red-600',
};

const statusLabels = {
  pending_approval: 'Pendiente de Aprobacion',
  approved: 'Aprobada - Esperando Pago',
  deposit_paid: 'Apartado Pagado',
  fully_paid: 'Pagada Completamente',
  expired: 'Expirada',
  rejected: 'Rechazada',
  cancelled: 'Cancelada',
};

function TimeCountdown({ expiresAt }) {
  const [timeLeft, setTimeLeft] = useState('');
  const [expired, setExpired] = useState(false);

  useEffect(() => {
    if (!expiresAt) return;
    const update = () => {
      const now = new Date();
      const exp = new Date(expiresAt.replace('Z', '+00:00'));
      const diff = exp - now;
      if (diff <= 0) {
        setTimeLeft('Tiempo agotado');
        setExpired(true);
        return;
      }
      const h = Math.floor(diff / 3600000);
      const m = Math.floor((diff % 3600000) / 60000);
      const s = Math.floor((diff % 60000) / 1000);
      setTimeLeft(`${h}h ${m}m ${s}s`);
    };
    update();
    const interval = setInterval(update, 1000);
    return () => clearInterval(interval);
  }, [expiresAt]);

  if (!expiresAt) return null;

  return (
    <div className={`flex items-center gap-2 text-sm ${expired ? 'text-red-600' : 'text-blue-700'}`}>
      {expired ? <AlertTriangle className="h-4 w-4" /> : <Clock className="h-4 w-4" />}
      <span className="font-mono">{timeLeft}</span>
    </div>
  );
}

function BankDetailsCard() {
  const [bankDetails, setBankDetails] = useState(null);
  const [copiedField, setCopiedField] = useState('');

  useEffect(() => {
    api.get('/bank-details').then((res) => setBankDetails(res.data)).catch(() => {});
  }, []);

  const copyValue = (key, value) => {
    navigator.clipboard.writeText(value);
    setCopiedField(key);
    setTimeout(() => setCopiedField(''), 2000);
  };

  if (!bankDetails) return null;

  return (
    <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mt-3">
      <p className="text-sm font-medium text-amber-800 mb-2">Datos para transferencia:</p>
      <div className="space-y-1.5 text-sm">
        {Object.entries(bankDetails).map(([key, value]) => (
          <div key={key} className="flex items-center justify-between">
            <span className="text-amber-700 capitalize">{key}:</span>
            <div className="flex items-center gap-1.5">
              <span className="font-medium text-amber-900">{value}</span>
              <button onClick={() => copyValue(key, value)} className="p-0.5 hover:bg-amber-100 rounded">
                {copiedField === key ? <Check className="h-3.5 w-3.5 text-green-600" /> : <Copy className="h-3.5 w-3.5 text-amber-500" />}
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

export default function MyReservationsPage() {
  const [reservations, setReservations] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchReservations();
  }, []);

  const fetchReservations = async () => {
    try {
      const response = await api.get('/reservations/me');
      setReservations(response.data);
    } catch (error) {
      console.error('Error fetching reservations:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#FAF7F2] flex items-center justify-center pt-20">
        <Loader2 className="h-8 w-8 animate-spin text-[#5C4033]" />
      </div>
    );
  }

  return (
    <div data-testid="my-reservations-page" className="min-h-screen bg-[#FAF7F2] pt-20">
      <div className="max-w-4xl mx-auto px-4 py-12">
        <h1 className="font-serif text-3xl text-[#1A1A1A] mb-8">Mis Reservaciones</h1>

        {reservations.length === 0 ? (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-white rounded-2xl p-12 text-center shadow-sm"
          >
            <Calendar className="h-16 w-16 text-[#C8962D] mx-auto mb-4" />
            <h2 className="font-serif text-2xl text-[#1A1A1A] mb-2">No tienes reservaciones</h2>
            <p className="text-[#1A1A1A]/60 mb-6">Es el momento perfecto para planear tu escapada a La Finca</p>
            <Link to="/reservar">
              <Button className="bg-[#5C4033] hover:bg-[#3D5A3E] text-white">
                Hacer una Reservacion
                <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </Link>
          </motion.div>
        ) : (
          <div className="space-y-6">
            {reservations.map((reservation, index) => (
              <motion.div
                key={reservation.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                className="bg-white rounded-2xl p-6 shadow-sm"
                data-testid={`reservation-card-${reservation.id}`}
              >
                <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-4">
                  <div>
                    <div className="flex items-center gap-3 mb-1">
                      <h3 className="font-serif text-xl text-[#1A1A1A]">
                        {reservation.reservation_type === 'hospedaje' ? 'Hospedaje' : 'Renta de Dia'}
                      </h3>
                      <Badge className={statusColors[reservation.status] || 'bg-gray-100 text-gray-600'}>
                        {statusLabels[reservation.status] || reservation.status}
                      </Badge>
                    </div>
                    <p className="text-xs text-[#1A1A1A]/50">ID: {reservation.id.slice(0, 8)}...</p>
                  </div>
                  <div className="text-right mt-3 md:mt-0">
                    <p className="font-serif text-2xl text-[#5C4033]">{formatCurrency(reservation.total)}</p>
                    {reservation.amount_paid > 0 && (
                      <p className="text-sm text-green-600">
                        Pagado: {formatCurrency(reservation.amount_paid)}
                      </p>
                    )}
                    {reservation.amount_remaining > 0 && reservation.status !== 'expired' && reservation.status !== 'rejected' && (
                      <p className="text-sm text-amber-600">
                        Restante: {formatCurrency(reservation.amount_remaining)}
                      </p>
                    )}
                  </div>
                </div>

                <div className="grid sm:grid-cols-3 gap-4 py-4 border-t border-b border-gray-100">
                  <div className="flex items-center space-x-3">
                    <Calendar className="h-5 w-5 text-[#C8962D]" />
                    <div>
                      <p className="text-xs text-[#1A1A1A]/60">
                        {reservation.reservation_type === 'dia' ? 'Fecha' : 'Llegada'}
                      </p>
                      <p className="font-medium text-sm">{formatDate(reservation.check_in)}</p>
                    </div>
                  </div>
                  {reservation.check_out && (
                    <div className="flex items-center space-x-3">
                      <Clock className="h-5 w-5 text-[#C8962D]" />
                      <div>
                        <p className="text-xs text-[#1A1A1A]/60">Salida</p>
                        <p className="font-medium text-sm">{formatDate(reservation.check_out)}</p>
                      </div>
                    </div>
                  )}
                  <div className="flex items-center space-x-3">
                    <Users className="h-5 w-5 text-[#C8962D]" />
                    <div>
                      <p className="text-xs text-[#1A1A1A]/60">Huespedes</p>
                      <p className="font-medium text-sm">{reservation.guests}</p>
                    </div>
                  </div>
                </div>

                {/* Status-specific messaging */}
                {reservation.status === 'pending_approval' && (
                  <div className="mt-4 p-3 bg-amber-50 border border-amber-200 rounded-lg">
                    <p className="text-sm text-amber-800">
                      Tu solicitud esta siendo revisada. Te notificaremos cuando sea aprobada.
                    </p>
                  </div>
                )}

                {reservation.status === 'approved' && reservation.expires_at && (
                  <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                    <div className="flex items-center justify-between mb-2">
                      <p className="text-sm font-medium text-blue-800">
                        Reservacion aprobada - Realiza tu apartado
                      </p>
                      <TimeCountdown expiresAt={reservation.expires_at} />
                    </div>
                    <p className="text-sm text-blue-700">
                      Deposita <span className="font-semibold">{formatCurrency(reservation.deposit_amount)}</span> para confirmar tu reservacion. 
                      Envia tu comprobante por WhatsApp.
                    </p>
                    <BankDetailsCard />
                  </div>
                )}

                {reservation.status === 'deposit_paid' && (
                  <div className="mt-4 p-3 bg-emerald-50 border border-emerald-200 rounded-lg flex items-start gap-2">
                    <CheckCircle className="h-5 w-5 text-emerald-600 flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="text-sm font-medium text-emerald-800">Apartado confirmado</p>
                      <p className="text-sm text-emerald-700">
                        Tu fecha esta reservada. El saldo restante de {formatCurrency(reservation.amount_remaining)} se paga al llegar o antes de tu estancia.
                      </p>
                    </div>
                  </div>
                )}

                {reservation.status === 'fully_paid' && (
                  <div className="mt-4 p-3 bg-green-50 border border-green-200 rounded-lg flex items-start gap-2">
                    <CheckCircle className="h-5 w-5 text-green-600 flex-shrink-0 mt-0.5" />
                    <p className="text-sm text-green-800 font-medium">
                      Pago completo confirmado. Te esperamos en La Finca.
                    </p>
                  </div>
                )}

                {reservation.status === 'rejected' && (
                  <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-lg">
                    <p className="text-sm text-red-700">
                      Lo sentimos, esta solicitud fue rechazada.
                      {reservation.rejection_reason && ` Razon: ${reservation.rejection_reason}`}
                    </p>
                  </div>
                )}

                {reservation.status === 'expired' && (
                  <div className="mt-4 p-3 bg-gray-50 border border-gray-200 rounded-lg">
                    <p className="text-sm text-gray-600">
                      Esta reservacion expiro por no recibir el pago en el tiempo establecido.
                    </p>
                  </div>
                )}

                {reservation.special_requests && (
                  <div className="mt-3">
                    <p className="text-xs text-[#1A1A1A]/50">Peticiones especiales:</p>
                    <p className="text-sm text-[#1A1A1A]/70">{reservation.special_requests}</p>
                  </div>
                )}

                <p className="text-xs text-[#1A1A1A]/40 mt-3">
                  Solicitada el {formatDate(reservation.created_at)}
                </p>
              </motion.div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
